import { store } from '../state.js';
import { formatDateTime, daysAgo, escapeHtml } from '../utils.js';
import { maskPhone } from '../store.js';
import { DIRECTIONS_BY_ID, APPLIANCE_DIRECTIONS } from '../directionsData.js';
import { CITIES } from '../data.js';
import { renderLeadPanel } from '../leadPanel.js';

const PAGE_SIZE = 40;

let filters = {
    query: '',
    period: 'all',
    status: 'all',
    direction: 'all',
    city: 'all'
};
let visibleCount = PAGE_SIZE;

function normalize(str) {
    return (str || '').toString().toLowerCase().replace(/ё/g, 'е');
}

function matchesFilters(lead) {
    if (filters.period !== 'all') {
        const cutoff = daysAgo(Number(filters.period));
        if (lead.createdAt < cutoff) return false;
    }
    if (filters.status !== 'all') {
        if (filters.status === 'review') {
            if (!lead.dispute || lead.dispute.status !== 'review') return false;
        } else if (lead.status !== filters.status) {
            return false;
        }
    }
    if (filters.direction !== 'all' && lead.directionId !== filters.direction) return false;
    if (filters.city !== 'all' && lead.cityId !== filters.city) return false;

    if (filters.query) {
        const q = normalize(filters.query);
        const haystack = normalize([
            lead.name, lead.phone, lead.number, lead.place,
            lead.example, lead.note
        ].join(' '));
        if (!haystack.includes(q)) return false;
    }

    return true;
}

function statusLabel(lead) {
    if (lead.dispute && lead.dispute.status === 'review') return 'На проверке';
    const map = { new: 'Новая', progress: 'В работе', called: 'Дозвонились', deal: 'Сделка', declined: 'Отказ клиента' };
    return map[lead.status] || lead.status;
}

export function renderLeads(container) {
    const state = store.get();
    const filtered = state.leads.filter(matchesFilters);
    const visible = filtered.slice(0, visibleCount);

    container.innerHTML = `
        <div class="leads-filters">
            <input type="text" id="leads-search" placeholder="Имя, телефон, номер, город или текст" value="${escapeHtml(filters.query)}">
            <select id="leads-period">
                <option value="all" ${filters.period === 'all' ? 'selected' : ''}>Весь период</option>
                <option value="1" ${filters.period === '1' ? 'selected' : ''}>Сегодня</option>
                <option value="7" ${filters.period === '7' ? 'selected' : ''}>7 дней</option>
                <option value="30" ${filters.period === '30' ? 'selected' : ''}>30 дней</option>
            </select>
            <select id="leads-status">
                <option value="all">Все статусы</option>
                <option value="new" ${filters.status === 'new' ? 'selected' : ''}>Новая</option>
                <option value="progress" ${filters.status === 'progress' ? 'selected' : ''}>В работе</option>
                <option value="called" ${filters.status === 'called' ? 'selected' : ''}>Дозвонились</option>
                <option value="deal" ${filters.status === 'deal' ? 'selected' : ''}>Сделка</option>
                <option value="declined" ${filters.status === 'declined' ? 'selected' : ''}>Отказ клиента</option>
                <option value="review" ${filters.status === 'review' ? 'selected' : ''}>На проверке</option>
            </select>
            <select id="leads-direction">
                <option value="all">Все направления</option>
                ${APPLIANCE_DIRECTIONS.map(d => `<option value="${d.id}" ${filters.direction === d.id ? 'selected' : ''}>${d.title}</option>`).join('')}
            </select>
            <select id="leads-city">
                <option value="all">Все города</option>
                ${CITIES.map(c => `<option value="${c.id}" ${filters.city === c.id ? 'selected' : ''}>${c.name}</option>`).join('')}
            </select>
        </div>

        <div class="leads-count">Найдено: ${filtered.length}</div>

        ${filtered.length ? `
            <div class="leads-table">
                <div class="leads-table__head">
                    <div>Время и №</div>
                    <div>Клиент</div>
                    <div>Направление</div>
                    <div>География</div>
                    <div>Источник</div>
                    <div>Статус</div>
                </div>
                <div class="leads-table__body" id="leads-table-body">
                    ${visible.map(rowMarkup).join('')}
                </div>
            </div>
            ${filtered.length > visible.length ? `<button type="button" class="leads-load-more" id="leads-load-more">Показать ещё</button>` : ''}
        ` : `
            <div class="leads-empty">
                <p>Заявок с такими условиями нет</p>
                <button type="button" class="leads-empty__reset" id="leads-reset">Сбросить фильтры</button>
            </div>
        `}
    `;

    const searchInput = container.querySelector('#leads-search');
    let searchDebounce;
    searchInput.addEventListener('input', () => {
        clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
            filters.query = searchInput.value;
            visibleCount = PAGE_SIZE;
            renderLeads(container);
            container.querySelector('#leads-search').focus();
            const val = container.querySelector('#leads-search').value;
            container.querySelector('#leads-search').setSelectionRange(val.length, val.length);
        }, 250);
    });

    ['period', 'status', 'direction', 'city'].forEach(key => {
        const el = container.querySelector(`#leads-${key}`);
        if (el) el.addEventListener('change', () => {
            filters[key] = el.value;
            visibleCount = PAGE_SIZE;
            renderLeads(container);
        });
    });

    const resetBtn = container.querySelector('#leads-reset');
    if (resetBtn) resetBtn.addEventListener('click', () => {
        filters = { query: '', period: 'all', status: 'all', direction: 'all', city: 'all' };
        visibleCount = PAGE_SIZE;
        renderLeads(container);
    });

    const loadMoreBtn = container.querySelector('#leads-load-more');
    if (loadMoreBtn) loadMoreBtn.addEventListener('click', () => {
        visibleCount += PAGE_SIZE;
        renderLeads(container);
    });

    const body = container.querySelector('#leads-table-body');
    if (body) {
        body.addEventListener('click', e => {
            const row = e.target.closest('.leads-table__row');
            if (row) renderLeadPanel(row.dataset.id);
        });
    }
}

function rowMarkup(lead) {
    const direction = DIRECTIONS_BY_ID[lead.directionId];
    const city = CITIES.find(c => c.id === lead.cityId);
    const isNew = lead.status === 'new' && !lead.dispute;

    return `
        <div class="leads-table__row${isNew ? ' leads-table__row--new' : ''}" data-id="${lead.id}">
            <div>
                <div class="leads-table__num">№${lead.number}</div>
                <div class="leads-table__time">${formatDateTime(lead.createdAt)}</div>
            </div>
            <div>
                <div class="leads-table__name">${escapeHtml(lead.name)}</div>
                <div class="leads-table__phone">${maskPhone(lead.phone)}</div>
            </div>
            <div>${direction ? direction.title : lead.directionId}</div>
            <div>
                <div>${city ? city.name : lead.cityId}</div>
                <div class="leads-table__district">${lead.place}</div>
            </div>
            <div>${lead.source}</div>
            <div><span class="leads-table__status leads-table__status--${lead.dispute && lead.dispute.status === 'review' ? 'review' : lead.status}">${statusLabel(lead)}</span></div>
        </div>
    `;
}

export function refreshLeadsBadge() {
    const badge = document.querySelector('#nav-leads-badge');
    if (!badge) return;
    const count = store.get().leads.filter(l => l.status === 'new' && !l.dispute).length;
    if (count > 0) {
        badge.textContent = count > 99 ? '99+' : count;
        badge.hidden = false;
    } else {
        badge.hidden = true;
    }
}
